import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { NON_DIGITS_PATTERN } from '@shared-constants/pattern-constants';

import { environment } from '../../../../environments/environment';
import { ApiService } from '../../../services/api.service';
import { DatahubService } from '../../../services/datahub.service';
import { LogRocketService } from '../../../services/log-rocket.service';
import { MessageService } from '../../../services/message.service';
import { ModalComponent } from '../modal.component';

@Component({
  selector: 'app-modal-content-need-help',
  templateUrl: './modal-content-need-help.component.html',
  styleUrl: './modal-content-need-help.component.css',
  standalone: false,
})
export class ModalContentNeedHelpComponent implements OnInit {
  public readonly form: FormGroup = new FormGroup({
    message: new FormControl('', [Validators.required]),
    //Regex pattern matches 10 digits
    //via: https://stackoverflow.com/a/2015956/79677
    phoneNumber: new FormControl('', [Validators.pattern(/^\D*(\d\D*){10}$/)]),
    email: new FormControl('', [Validators.email]),
  });

  public shouldShowPhoneOrEmailMessage = false;
  private logRocketUrl = '';
  public isSubmitTextShown = true;
  public isSubmitButtonDisabled = false;

  @Output() public readonly closed = new EventEmitter();
  @Input() public messageSeed?: string;
  @Input() public emailAddressOverride?: string;
  @Input() public subjectOverride?: string;

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly apiService: ApiService,
    private readonly messageService: MessageService,
    private readonly dialogRef: MatDialogRef<ModalComponent>,
    private readonly datahub: DatahubService,
    private readonly logRocketService: LogRocketService,
  ) {}

  public ngOnInit(): void {
    if (this.messageSeed) {
      this.form.get('message').setValue(this.messageSeed);
    }
    this.dialogRef.backdropClick().subscribe({
      next: () => {
        this.closed.emit(null);
      },
      error: (err) => {
        console.error('Error in backdropClick subscription:', err);
      },
    });
    this.logRocketUrl = this.logRocketService.url;
  }

  public formatPhoneNumber(): void {
    //remove any characters that are not numbers, then format the numbers into a common format
    //This means "1234567890" becomes "(123) 456-7890"
    const phoneDigitsOnlyStr: string = this.form.controls['phoneNumber'].value.replace(NON_DIGITS_PATTERN, '');

    //only append these formatted things if the numbers exist
    //this way the spaces and dashes are only added when there are enough numbers for it to be in the correct place
    let formattedNumberStr = '';
    const firstThreeNumbers = phoneDigitsOnlyStr.slice(0, 3);
    if (firstThreeNumbers.length === 3) {
      formattedNumberStr += `(${firstThreeNumbers})`;
    } else {
      formattedNumberStr = phoneDigitsOnlyStr;
    }

    const nextThreeNumbers = phoneDigitsOnlyStr.slice(3, 6);
    if (nextThreeNumbers.length > 0) {
      formattedNumberStr += ` ${nextThreeNumbers}`;
    }

    const lastNumbers = phoneDigitsOnlyStr.slice(6, phoneDigitsOnlyStr.length);
    if (lastNumbers.length > 0) {
      formattedNumberStr += `-${lastNumbers}`;
    }

    this.form.controls['phoneNumber'].setValue(formattedNumberStr);
  }

  public submit(): void {
    this.formatPhoneNumber();
    this.isSubmitTextShown = false;
    this.isSubmitButtonDisabled = true;

    //These fields are not individually required, but if they are BOTH blank then we will show a special message
    this.shouldShowPhoneOrEmailMessage =
      this.form.controls['email'].value === '' && this.form.controls['phoneNumber'].value === '';

    //If the form is valid based on the validators and at least one contact method is filled in (validity already checked)
    if (this.form.touched && this.form.valid && !this.shouldShowPhoneOrEmailMessage) {
      let name = this.datahub.user.getFullName();
      if (this.datahub.user.userName !== this.datahub.user.approvingName) {
        name += ` (working as ${this.datahub.user.approvingName})`;
      }

      const message = this.buildMessage(name);
      const recipientEmail = this.emailAddressOverride ?? environment.defaultSupportEmailAddress;
      const subject = this.subjectOverride ?? `Feedback Submitted`;

      // Disabling form needs to be done here, when form is disabled, the values aren't submitted.
      // So disable has to be done after the logic that uses this.form.control values
      this.form.disable();
      this.apiService
        .sendEmail$(message, recipientEmail, subject)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.messageService.info('We will get back to you soon during regular business hours', 'Thank you for contacting us');
            this.isSubmitTextShown = true;
            this.isSubmitButtonDisabled = false;
            this.form.enable();
            this.closed.emit(null);
          },
          error: (failed: unknown) => {
            this.messageService.error(
              "We're sorry, something went wrong and your support message could not be sent. Please try again.",
            );
            console.error(failed);
            this.isSubmitTextShown = true;
            this.isSubmitButtonDisabled = false;
            this.form.enable();
          },
        });
    }
  }

  private buildMessage(name: string): string {
    // getRawValue is needed since the form is disabled/enabled
    const noneProvided = '(none provided)';
    const message = this.form.controls['message'].getRawValue() as string;
    const email = (this.form.controls['email'].getRawValue() as string) || noneProvided;
    const phone = (this.form.controls['phoneNumber'].getRawValue() as string) || noneProvided;

    return `${message}
---------------------------------------
Name: ${name}
Email: ${email}
Phone: ${phone}
---------------------------------------
Employee #: ${this.datahub.user.getEmpId()}
Cost Center: ${this.datahub.user.getHomeCC()}
Pay Type: ${this.datahub.user.getPayType()}
Work Schedule Rule: ${this.datahub.user.getWorkScheduleRule()}
---------------------------------------
Application: ${environment.application.toUpperCase()} (in the ${environment.environmentNameFull} environment)
LogRocket: ${this.logRocketUrl || '(none)'}`;
  }
}
